// One-click preset content for the "Lake Day Adventure Pack" guide pages.
// Uses [label](url) markdown links, ALL-CAPS section headers, "- " bullets and "---" dividers.

const SHARED = `
---
DO I NEED A TONTO PASS?
Possibly — and this is important to check before leaving.
Many developed recreation locations within Tonto National Forest require a recreation pass.
Depending on where you're going and what you're doing, this can include:
Tonto Daily Pass
Used at many developed recreation sites.
Watercraft Sticker
Boaters may need an additional watercraft sticker along with their Tonto Daily Pass.
Tonto Discovery Pass
Another Forest Service pass option for frequent visitors.
Fee requirements can vary by recreation site. Always check the specific recreation area before your trip.
Passes & where to buy: [Tonto Passes & Permits](https://www.fs.usda.gov/r03/tonto/passes)

---
WHAT TO BRING
Picnic Day
- Food & snacks
- Plenty of drinking water
- Picnic blanket
- Chairs
- Small table
- Cups & utensils
- Paper towels
- Trash bags
- Cooler & ice
- Sunscreen
- Hat
- Sunglasses
Lake Day (add):
- Swimsuits
- Towels
- Water shoes
- Life jackets / PFDs
- Waterproof phone pouch
- Dry bag
- Extra drinking water
- Change of clothes

---
DESERT & LAKE SAFETY
Arizona's desert lakes are beautiful, but conditions can become dangerous quickly.
HEAT
Summer temperatures can become extreme. Bring significantly more water than you think you'll need and avoid strenuous activities during the hottest part of the day.
SUN
Use sunscreen and reapply regularly. A wide-brimmed hat and sunglasses are highly recommended.
WILDLIFE
You may encounter:
- Rattlesnakes
- Scorpions
- Bees
- Coyotes
- Javelina
- Bighorn sheep
Observe wildlife from a distance and never feed wild animals.
WATER SAFETY
Children and inexperienced swimmers should wear properly fitted personal flotation devices. Arizona lakes can have sudden depth changes and underwater hazards.

---
LEAVE IT BETTER THAN YOU FOUND IT
Tonto National Forest follows a Pack It In — Pack It Out approach.
Please:
- Take your trash with you.
- Don't leave food behind.
- Don't feed wildlife.
- Respect vegetation.
- Follow fire restrictions.
- Keep glass away from prohibited recreation areas.
Glass containers are prohibited in the Canyon Lake Recreation Area.

---
BEFORE YOU LEAVE
Check:
- [ ] Lake / recreation area is open
- [ ] Current weather
- [ ] Tonto National Forest alerts
- [ ] Fire restrictions
- [ ] Required recreation pass
- [ ] Boat launch status, if boating
- [ ] Fishing license, if fishing
- [ ] Plenty of drinking water
- [ ] Sunscreen
- [ ] Life jackets / PFDs
- [ ] Phone is charged

---
HELPFUL LINKS
- [Tonto Passes & Permits](https://www.fs.usda.gov/r03/tonto/passes)
- [Tonto National Forest Alerts](https://www.fs.usda.gov/r03/tonto/alerts)
- [Arizona Game & Fish — Fishing License](https://www.azgfd.com/license/)
- [Canyon Lake — Tonto National Forest](https://www.fs.usda.gov/r03/tonto/recreation/canyon-lake)
- [Canyon Lake Marina](https://canyonlakemarina.com/)
- [The Dolly Steamboat](https://dollysteamboat.com/)
- [Saguaro Lake Marina](https://saguarolakemarina.com/)
- [Desert Belle Cruises](https://desertbellecruises.com/)
`;

const CANYON = `Canyon Lake sits among towering desert cliffs in the Tonto National Forest. The eastern portion winds through steep canyon walls and is particularly scenic. Bighorn sheep and other desert wildlife are sometimes spotted along the surrounding cliffs.

GETTING THERE
From Apache Junction, take State Route 88 / Apache Trail north toward Canyon Lake.
The road is paved to Canyon Lake but becomes winding as you approach the lake. Allow extra time and enjoy the scenery.
Tip: Summer weekends and holidays can be extremely busy. Canyon Lake can reach its boating capacity early in the day, so an early start is recommended.
More info: [Canyon Lake — Tonto National Forest](https://www.fs.usda.gov/r03/tonto/recreation/canyon-lake)

---
WHERE TO PICNIC & SWIM
Acacia Recreation Site
Our pick for a picnic and swimming day.
Acacia has a gentle swimming beach along Canyon Lake and is a great choice for guests bringing the Picnic Adventure Pack.
Good for:
- Picnics
- Swimming
- Relaxing along the shoreline
- Families
- Scenic views
Map: [Acacia Recreation Site](https://maps.google.com/?q=Acacia+Recreation+Site+Canyon+Lake+AZ)

Boulder Recreation Site
A good choice if fishing is part of your day.
Features include:
- Shore access
- Picnic opportunities
- Floating fishing pier
- Lake views
Map: [Boulder Recreation Site](https://maps.google.com/?q=Boulder+Recreation+Site+Canyon+Lake+AZ)

---
BOATING & KAYAKING
Canyon Lake allows both motorized and non-motorized boating.
Popular activities include:
- Kayaking
- Paddleboarding
- Canoeing
- Motor boating
- Waterskiing
- Fishing
Boaters: Travel around Canyon Lake is counter-clockwise. Always follow posted buoys and water markers.
Boat rentals: [Canyon Lake Marina](https://canyonlakemarina.com/)

---
FISHING
Canyon Lake is well known for fishing.
Fish can include:
- Largemouth bass
- Yellow bass
- Rainbow trout
- Catfish
- Sunfish
The Arizona Game & Fish Department stocks Canyon Lake with rainbow trout during portions of the cooler season.
A fishing pier is located at Boulder Recreation Site.
An Arizona fishing license is generally required for anglers age 10 and older.
Get a license: [Arizona Game & Fish — Licenses](https://www.azgfd.com/license/)

---
TAKE A CRUISE
The Dolly Steamboat
Take a scenic cruise through the dramatic canyon walls of Canyon Lake aboard the Dolly Steamboat.
This is one of the area's signature experiences and a great alternative for guests who want to experience Canyon Lake without renting a boat.
Reservations are recommended.
Book: [dollysteamboat.com](https://dollysteamboat.com/)

---
CANYON LAKE MARINA
Canyon Lake Marina & Campground
16802 AZ-88, Apache Junction, AZ 85119
The marina area provides access to services that can include:
- Boat launching
- Boat rentals
- Marina services
- Food
- Camping
- Fishing
- Lake supplies
Website: [canyonlakemarina.com](https://canyonlakemarina.com/)
Map: [Canyon Lake Marina & Campground](https://maps.google.com/?q=Canyon+Lake+Marina+%26+Campground+16802+AZ-88+Apache+Junction+AZ+85119)

---
CAMPING
Camping opportunities are available around Canyon Lake.
Some recreation and camping fees are separate from the Tonto Daily Pass.
If camping is part of your trip, verify the individual campground's requirements before leaving.

---
IMPORTANT CANYON LAKE NOTICE — FALL 2026
A major Canyon Lake drawdown project is scheduled for September 2026.
SRP says the Forest Service plans to close Canyon Lake, including boat ramps and access points, beginning September 8, 2026, in preparation for lowering the lake beginning September 16.
The drawdown is expected to last approximately 16 weeks for dam safety inspections and maintenance.
If your stay falls during this period, consider Saguaro Lake instead.
Always verify current conditions before traveling.`;

const SAGUARO = `Saguaro Lake is surrounded by classic Sonoran Desert scenery — mountains, canyon walls and hillsides covered with giant saguaros. The lake has approximately 22 miles of shoreline.

---
BUTCHER JONES BEACH
Our pick for a Saguaro Lake picnic & beach day.
Butcher Jones is one of the most popular recreation areas at Saguaro Lake.
It's especially good for:
- Swimming
- Picnics
- Families
- Kayaking
- Paddleboarding
- Fishing
- Hiking
- Photography
The beach is located within a no-wake area, making this one of the better locations for relaxing near the water.
Mesquite trees provide some natural shade, but shade can be limited. Bring plenty of drinking water.
Map: [Butcher Jones Beach](https://maps.google.com/?q=Butcher+Jones+Recreation+Site+Saguaro+Lake+AZ)

---
BUTCHER JONES TRAIL
Want to combine your lake day with a hike?
The Butcher Jones area provides access to a scenic trail running alongside portions of Saguaro Lake.
Expect:
- Saguaros
- Desert mountains
- Lake overlooks
- Excellent photo opportunities
Avoid strenuous desert hiking during extreme summer heat.

---
SAGUARO LAKE BOATING
Saguaro Lake is popular for:
- Powerboats
- Pontoon boats
- Kayaks
- Paddleboards
- Fishing boats
- Canoes
Boaters should always observe posted boating rules, no-wake zones and water markers.
Boat rentals: [Saguaro Lake Marina](https://saguarolakemarina.com/rentals/)

---
SAGUARO LAKE FISHING
Common fishing targets include:
- Largemouth bass
- Yellow bass
- Walleye
- Catfish
- Rainbow trout
Early morning and evening can be particularly enjoyable times to fish during warmer months.
Remember to obtain the appropriate Arizona fishing license.
Get a license: [Arizona Game & Fish — Licenses](https://www.azgfd.com/license/)

---
DESERT BELLE CRUISE
Don't have a boat?
Take a narrated cruise aboard Desert Belle Cruises.
Cruises depart from the Saguaro Lake Marina area and provide an easy way to experience the lake and surrounding canyon scenery.
Reservations are recommended.
Book: [desertbellecruises.com](https://desertbellecruises.com/)

---
SAGUARO LAKE MARINA
Saguaro Lake Marina
14011 N Bush Hwy, Mesa, AZ 85215
The marina is the primary hub for boating activity at Saguaro Lake.
Depending on availability and season, services can include:
- Boat rentals
- Boat launching
- Marina services
- Food and drinks
- Lake supplies
- Sightseeing cruises
Website: [saguarolakemarina.com](https://saguarolakemarina.com/)
Map: [Saguaro Lake Marina](https://maps.google.com/?q=Saguaro+Lake+Marina+14011+N+Bush+Hwy+Mesa+AZ+85215)`;

export const LAKE_DAY_PAGES = [
  {
    title: "Canyon Lake",
    subtitle: "Dramatic canyon scenery • Picnics • Swimming • Boating • Kayaking • Fishing • Cruises",
    content: CANYON + SHARED,
  },
  {
    title: "Saguaro Lake",
    subtitle: "Beach day • Picnics • Kayaking • Boating • Fishing • Hiking • Cruises",
    content: SAGUARO + SHARED,
  },
];
